import { useCallback, useEffect, useState } from 'react'
import { Box, Flex, Text, VStack, toaster } from '@minetrace/ui'
import { useNavigate, useParams } from 'react-router-dom'
import { LuArrowLeft, LuClipboardList, LuPlus } from 'react-icons/lu'
import { modificationRequestService, proveedoresService } from '../../api/services'
import type { ModificationRequestResponse } from '../../api/models/response/modificationRequest'
import type { ProveedorResponse } from '../../api/models/response/proveedor'
import { brand, themeColors } from '../../theme'
import { formatDateTime } from '../../lib/dates'

const STATUS_CONFIG: Record<number, { label: string; bg: string; color: string }> = {
  0: { label: 'Borrador', bg: 'rgba(107, 114, 128, 0.15)', color: '#857b71' },
  1: { label: 'Pendiente', bg: 'rgba(212, 154, 42, 0.15)', color: '#a8740e' },
  2: { label: 'Aprobada', bg: 'rgba(42, 138, 135, 0.15)', color: '#1d6664' },
  3: { label: 'Rechazada', bg: 'rgba(200, 68, 47, 0.15)', color: '#b23a26' },
  4: { label: 'Cancelada', bg: 'rgba(107, 114, 128, 0.15)', color: '#857b71' },
}

export default function HistorialModificacionesPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const colors = themeColors

  const [proveedor, setProveedor] = useState<ProveedorResponse | null>(null)
  const [items, setItems] = useState<ModificationRequestResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [newReason, setNewReason] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const pData = await proveedoresService.getById(id)
      setProveedor(pData)
      if (pData.cuid?.expedienteCode) {
        const mrs = await modificationRequestService.list(pData.cuid.expedienteCode)
        setItems(mrs)
      } else {
        setItems([])
      }
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al cargar' })
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  const handleCreate = async () => {
    if (!proveedor?.cuid?.expedienteCode) return
    if (!newReason.trim()) {
      toaster.error({ description: 'Debe ingresar un motivo' })
      return
    }
    setBusy(true)
    try {
      const mrId = await modificationRequestService.create(proveedor.cuid.expedienteCode, newReason.trim())
      toaster.success({ description: 'Solicitud creada. Abriendo editor...' })
      navigate(`/app/proveedores/${id}/modificar/${mrId}`)
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al crear la solicitud' })
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <Box p={8} textAlign="center"><Text fontSize="sm" color={colors.textMuted}>Cargando...</Text></Box>
  }
  if (!proveedor) {
    return <Box p={8} textAlign="center"><Text fontSize="sm" color={colors.text}>Proveedor no encontrado</Text></Box>
  }

  const hasExpediente = Boolean(proveedor.cuid?.expedienteCode)

  return (
    <VStack gap={6} alignItems="stretch">
      {/* Header */}
      <Flex alignItems="center" gap={3}>
        <Box as="button" type="button" p={2} borderRadius="xl" cursor="pointer"
          color={colors.textMuted}
          _hover={{ bg: colors.bgHover, color: colors.text }}
          onClick={() => navigate('/app/proveedores')}
        >
          <LuArrowLeft size={20} />
        </Box>
        <Box flex="1">
          <Flex alignItems="center" gap={2}>
            <LuClipboardList size={18} color={brand.green} />
            <Text fontSize="xl" fontWeight="bold" color={colors.text}>
              Solicitudes de modificación
            </Text>
          </Flex>
          <Flex gap={2} alignItems="center" mt={1} flexWrap="wrap">
            <Text fontSize="sm" fontWeight="600" color={colors.text}>{proveedor.legalName}</Text>
            <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace" bg="gray.100" px={2} py={0.5} borderRadius="md">
              RUC {proveedor.ruc}
            </Text>
          </Flex>
        </Box>
        {hasExpediente && !showCreate && (
          <Box
            as="button" type="button"
            display="inline-flex" alignItems="center" gap={2} px={4} py={2}
            bg={brand.green} color="white"
            borderRadius="md" fontSize="sm" fontWeight="700"
            cursor="pointer" _hover={{ opacity: 0.9 }}
            onClick={() => setShowCreate(true)}
          >
            <LuPlus size={16} />
            Nueva solicitud
          </Box>
        )}
      </Flex>

      {!hasExpediente && (
        <Box p={6} textAlign="center" borderRadius="xl" border="1px dashed" borderColor={colors.border}>
          <Text fontSize="sm" color={colors.textMuted}>
            Este proveedor aún no tiene expediente. Las solicitudes de modificación sólo aplican tras la aprobación.
          </Text>
        </Box>
      )}

      {hasExpediente && showCreate && (
        <Box
          p={4}
          bg={colors.bgCard}
          borderRadius="xl"
          border="1px solid"
          borderColor={colors.border}
        >
          <Text fontSize="xs" color={colors.textMuted} mb={2} fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">
            Motivo de la modificación
          </Text>
          <Box as="textarea"
            rows={3}
            value={newReason}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewReason(e.target.value)}
            placeholder="Describe brevemente el cambio que necesitas hacer..."
            w="100%" p={3} borderRadius="md"
            bg={colors.bgCard} border="1px solid" borderColor={colors.border}
            fontSize="sm" color={colors.text}
            _placeholder={{ color: colors.textMuted }}
            _focus={{ borderColor: brand.green, outline: 'none' }}
          />
          <Flex gap={2} mt={3} justifyContent="flex-end">
            <Box as="button" type="button"
              px={4} py={2} borderRadius="md"
              border="1px solid" borderColor={colors.border}
              fontSize="sm" fontWeight="600"
              cursor={busy ? 'not-allowed' : 'pointer'}
              opacity={busy ? 0.5 : 1}
              onClick={() => { setShowCreate(false); setNewReason('') }}
              disabled={busy}
            >
              Cancelar
            </Box>
            <Box as="button" type="button"
              px={4} py={2} borderRadius="md"
              bg={busy ? '#b5aa9d' : brand.green} color="white"
              fontSize="sm" fontWeight="700"
              cursor={busy ? 'not-allowed' : 'pointer'}
              _hover={busy ? {} : { opacity: 0.9 }}
              onClick={handleCreate}
              disabled={busy}
            >
              Crear borrador
            </Box>
          </Flex>
        </Box>
      )}

      {hasExpediente && items.length === 0 && !showCreate && (
        <Box p={8} textAlign="center" borderRadius="xl" border="1px dashed" borderColor={colors.border}>
          <Text fontSize="sm" color={colors.textMuted}>
            No hay solicitudes de modificación para este proveedor.
          </Text>
        </Box>
      )}

      {items.length > 0 && (
        <VStack gap={2} alignItems="stretch">
          {items.map(mr => {
            const s = STATUS_CONFIG[mr.requestStatus] ?? STATUS_CONFIG[0]!
            return (
              <Box
                key={mr.id}
                p={4}
                borderRadius="xl"
                border="1px solid"
                borderColor={colors.border}
                bg={colors.bgCard}
                cursor="pointer"
                _hover={{ borderColor: brand.green, bg: `${brand.green}05` }}
                transition="all 0.15s"
                onClick={() => navigate(`/app/proveedores/${id}/modificar/${mr.id}`)}
              >
                <Flex alignItems="flex-start" gap={3}>
                  <Box flex="1" minW={0}>
                    <Flex alignItems="center" gap={2} mb={1} flexWrap="wrap">
                      <Text fontSize="sm" fontWeight="700" color={colors.text} fontFamily="monospace">
                        {mr.code}
                      </Text>
                      <Box
                        px={2} py={0.5} borderRadius="md"
                        fontSize="10px" fontWeight="bold" textTransform="uppercase"
                        bg={s.bg} color={s.color}
                      >
                        {s.label}
                      </Box>
                      {mr.anchorOrder !== null && (
                        <Text fontSize="10px" color={colors.textMuted} fontFamily="monospace">
                          item #{mr.anchorOrder}
                        </Text>
                      )}
                    </Flex>
                    <Text fontSize="sm" color={colors.text} mb={1}>{mr.reason}</Text>
                    <Flex gap={3} flexWrap="wrap" fontSize="xs" color={colors.textMuted}>
                      <Text>
                        Solicitada: {formatDateTime(mr.requestedAt)}
                        {mr.requestedByUserName && ` · por ${mr.requestedByUserName}`}
                      </Text>
                      {mr.reviewedAt && (
                        <Text>
                          Revisada: {formatDateTime(mr.reviewedAt)}
                          {mr.reviewedByUserName && ` · ${mr.reviewedByUserName}`}
                        </Text>
                      )}
                    </Flex>
                    {mr.reviewNotes && (
                      <Text fontSize="xs" color={colors.textMuted} mt={1} fontStyle="italic">
                        "{mr.reviewNotes}"
                      </Text>
                    )}
                  </Box>
                  <Box color={colors.textMuted} pt={1}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </Box>
                </Flex>
              </Box>
            )
          })}
        </VStack>
      )}
    </VStack>
  )
}
